import {
    Box,
    Button,
    Flex,
    Icon,
    Popover,
    PopoverBody,
    PopoverContent,
    PopoverTrigger,
    Text,
    VStack,
} from '@chakra-ui/react';
import {
    TbBriefcase,
    TbChevronLeft,
    TbMapPin,
    TbUserCheck,
} from 'react-icons/tb';
import type { IconType } from 'react-icons';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { UserStatus } from '../../hooks/useGetSettings';
import type { Role } from '../../hooks/useValidateSession';
import type { LocationEntry } from '../../hooks/useGetLocations';
import { filterLocations } from '../../hooks/useGetLocations';
import { AddFilterMenu, ClearFiltersButton, FilterBar, FilterChip } from '../../ui';
import {
    CheckboxMenuRow,
    CheckboxMenuSearch,
    MenuRow,
    MenuFooterActions,
    TokenChip,
    checkboxMenuColors,
    checkboxMenuPopoverContentSx,
    checkboxMenuTriggerMetrics,
} from '../../ui/primitives/CheckboxMenu';

export type UsersFilterType = 'status' | 'location' | 'position';

export interface UsersListFilters {
    statuses?: UserStatus[];
    locationIds?: string[];
    positions?: Role[];
}

export interface UsersAdvancedFiltersProps {
    filters: UsersListFilters;
    onFiltersChange: (filters: UsersListFilters) => void;
    locations: LocationEntry[];
    positionOptions: Array<{ value: Role; label: string }>;
}

const STATUS_OPTIONS: Array<{ value: UserStatus; label: string }> = [
    { value: 'active', label: 'Active' },
    { value: 'pending', label: 'Pending' },
    { value: 'inactive', label: 'Inactive' },
];

const FILTER_CATEGORIES: { key: UsersFilterType; label: string; icon: IconType }[] = [
    { key: 'status', label: 'Status', icon: TbUserCheck },
    { key: 'location', label: 'Location', icon: TbMapPin },
    { key: 'position', label: 'Position', icon: TbBriefcase },
];

type OpenState = 'addFilter' | UsersFilterType | null;

const formatMultiChip = (label: string, values: string[]) => {
    if (values.length === 0) return label;
    if (values.length === 1) return `${label}: ${values[0]}`;
    return `${label}: ${values.length} selected`;
};

const UsersAdvancedFilters = ({
    filters,
    onFiltersChange,
    locations,
    positionOptions,
}: UsersAdvancedFiltersProps) => {
    const [openState, setOpenState] = useState<OpenState>(null);
    const [addFilterView, setAddFilterView] = useState<UsersFilterType | null>(null);
    const [tempFilters, setTempFilters] = useState<UsersListFilters>(filters);
    const [filterSearch, setFilterSearch] = useState('');
    const [locationSearch, setLocationSearch] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);

    const locationOptions = useMemo(
        () => filterLocations(locations, { excludeAll: true, activeOnly: true }),
        [locations],
    );

    const filteredLocationOptions = useMemo(() => {
        const q = locationSearch.trim().toLowerCase();
        if (!q) return locationOptions;
        return locationOptions.filter((loc) => loc.title.toLowerCase().includes(q));
    }, [locationOptions, locationSearch]);

    const closePopover = () => {
        setOpenState(null);
        setAddFilterView(null);
        setFilterSearch('');
        setLocationSearch('');
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (!openState) return;
            const target = event.target as HTMLElement;
            if (containerRef.current?.contains(target)) return;
            if (target.closest('[data-filter-popover]')) return;
            closePopover();
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [openState]);

    const isFilterActive = (key: UsersFilterType, source: UsersListFilters = filters) => {
        if (key === 'status') return (source.statuses?.length ?? 0) > 0;
        if (key === 'location') return (source.locationIds?.length ?? 0) > 0;
        return (source.positions?.length ?? 0) > 0;
    };

    const activeFilters = FILTER_CATEGORIES.filter((cat) => isFilterActive(cat.key));

    const togglePopover = (target: OpenState) => {
        if (openState === target) {
            closePopover();
        } else {
            setTempFilters(filters);
            setOpenState(target);
            setAddFilterView(null);
            setFilterSearch('');
            setLocationSearch('');
        }
    };

    const applyFilters = () => {
        onFiltersChange(tempFilters);
        closePopover();
    };

    const clearFilter = (key: UsersFilterType) => {
        const next = { ...filters };
        if (key === 'status') delete next.statuses;
        else if (key === 'location') delete next.locationIds;
        else delete next.positions;
        onFiltersChange(next);
        if (openState === key) closePopover();
    };

    const clearAllFilters = () => {
        onFiltersChange({});
        closePopover();
    };

    const getChipLabel = (key: UsersFilterType) => {
        if (key === 'status') {
            const labels = (filters.statuses ?? []).map(
                (s) => STATUS_OPTIONS.find((o) => o.value === s)?.label ?? s,
            );
            return formatMultiChip('Status', labels);
        }
        if (key === 'location') {
            const labels = (filters.locationIds ?? []).map(
                (id) => locationOptions.find((l) => String(l._id) === id)?.title ?? id,
            );
            return formatMultiChip('Location', labels);
        }
        const labels = (filters.positions ?? []).map(
            (p) => positionOptions.find((o) => o.value === p)?.label ?? p,
        );
        return formatMultiChip('Position', labels);
    };

    /** Value preview on a drill-in row — the chip label without its category prefix. */
    const getCategorySummary = (key: UsersFilterType): string | undefined => {
        if (!isFilterActive(key)) return undefined;
        const label = getChipLabel(key);
        const separator = label.indexOf(': ');
        return separator === -1 ? label : label.slice(separator + 2);
    };

    const filteredCategories = FILTER_CATEGORIES.filter((c) =>
        c.label.toLowerCase().includes(filterSearch.toLowerCase()),
    );

    const renderFooter = (showReset: boolean = true) => (
        <MenuFooterActions
            secondaryLabel={showReset ? 'Reset' : 'Cancel'}
            onSecondary={showReset ? () => setTempFilters({}) : closePopover}
            onApply={applyFilters}
        />
    );

    const renderFilterEditor = (key: UsersFilterType) => {
        if (key === 'status') {
            const selectedStatuses = tempFilters.statuses ?? [];
            const toggleStatus = (value: UserStatus) => {
                const next = selectedStatuses.includes(value)
                    ? selectedStatuses.filter((entry) => entry !== value)
                    : [...selectedStatuses, value];
                setTempFilters({
                    ...tempFilters,
                    statuses: next.length > 0 ? next : undefined,
                });
            };
            return (
                <VStack align="stretch" spacing={0} p="6px">
                    {STATUS_OPTIONS.map((option) => (
                        <CheckboxMenuRow
                            key={option.value}
                            isSelected={selectedStatuses.includes(option.value)}
                            onClick={() => toggleStatus(option.value)}
                        >
                            {option.label}
                        </CheckboxMenuRow>
                    ))}
                </VStack>
            );
        }

        if (key === 'location') {
            const selectedLocationIds = tempFilters.locationIds ?? [];
            const toggleLocation = (id: string) => {
                const next = selectedLocationIds.includes(id)
                    ? selectedLocationIds.filter((entry) => entry !== id)
                    : [...selectedLocationIds, id];
                setTempFilters({
                    ...tempFilters,
                    locationIds: next.length > 0 ? next : undefined,
                });
            };
            return (
                <>
                    {selectedLocationIds.length > 0 && (
                        <Flex wrap="wrap" gap="5px" px="10px" pt="8px" pb="4px" maxH="80px" overflowY="auto">
                            {selectedLocationIds.map((id) => {
                                const loc = locationOptions.find((l) => String(l._id) === id);
                                return loc ? (
                                    <TokenChip key={id} onRemove={() => toggleLocation(id)}>
                                        {loc.title}
                                    </TokenChip>
                                ) : null;
                            })}
                        </Flex>
                    )}
                    <CheckboxMenuSearch
                        value={locationSearch}
                        onChange={setLocationSearch}
                        placeholder="Search locations..."
                    />
                    <VStack align="stretch" spacing={0} p="6px" maxH="250px" overflowY="auto">
                        {locationOptions.length === 0 || filteredLocationOptions.length === 0 ? (
                            <Text
                                fontSize={checkboxMenuTriggerMetrics.fontSize}
                                color={checkboxMenuColors.muted}
                                px="10px"
                                py="8px"
                            >
                                {locationOptions.length === 0 ? 'No locations found' : 'No matches found'}
                            </Text>
                        ) : (
                            filteredLocationOptions.map((loc) => (
                                <CheckboxMenuRow
                                    key={String(loc._id)}
                                    isSelected={selectedLocationIds.includes(String(loc._id))}
                                    onClick={() => toggleLocation(String(loc._id))}
                                >
                                    {loc.title}
                                </CheckboxMenuRow>
                            ))
                        )}
                    </VStack>
                </>
            );
        }

        const selectedPositions = tempFilters.positions ?? [];
        const togglePosition = (value: Role) => {
            const next = selectedPositions.includes(value)
                ? selectedPositions.filter((entry) => entry !== value)
                : [...selectedPositions, value];
            setTempFilters({
                ...tempFilters,
                positions: next.length > 0 ? next : undefined,
            });
        };
        return (
            <VStack align="stretch" spacing={0} p="6px" maxH="250px" overflowY="auto">
                {positionOptions.length === 0 ? (
                    <Text
                        fontSize={checkboxMenuTriggerMetrics.fontSize}
                        color={checkboxMenuColors.muted}
                        px="10px"
                        py="8px"
                    >
                        No positions found
                    </Text>
                ) : (
                    positionOptions.map((option) => (
                        <CheckboxMenuRow
                            key={option.value}
                            isSelected={selectedPositions.includes(option.value)}
                            onClick={() => togglePosition(option.value)}
                        >
                            {option.label}
                        </CheckboxMenuRow>
                    ))
                )}
            </VStack>
        );
    };

    const renderDrillInPanel = (key: UsersFilterType, showBack: boolean) => (
        <>
            <Box px={2} py={2} borderBottom="1px solid" borderColor="gray.100">
                {showBack ? (
                    <Button
                        size="xs"
                        variant="ghost"
                        onClick={() => {
                            setAddFilterView(null);
                            setLocationSearch('');
                        }}
                        leftIcon={<Icon as={TbChevronLeft as React.ElementType} />}
                        color="gray.600"
                        fontWeight="medium"
                    >
                        {FILTER_CATEGORIES.find((c) => c.key === key)?.label}
                    </Button>
                ) : (
                    <Text
                        fontSize="xs"
                        fontWeight="semibold"
                        color="gray.500"
                        textTransform="uppercase"
                        px={1}
                    >
                        {FILTER_CATEGORIES.find((c) => c.key === key)?.label}
                    </Text>
                )}
            </Box>
            {renderFilterEditor(key)}
        </>
    );

    return (
        <Box ref={containerRef}>
            <FilterBar>
                {activeFilters.map((cat) => (
                    <Popover
                        key={cat.key}
                        isOpen={openState === cat.key}
                        placement="bottom-end"
                        closeOnBlur={false}
                    >
                        <PopoverTrigger>
                            <FilterChip
                                icon={<Icon as={cat.icon as React.ElementType} boxSize={4} />}
                                label={getChipLabel(cat.key)}
                                onRemove={() => clearFilter(cat.key)}
                                onClick={() => togglePopover(cat.key)}
                            />
                        </PopoverTrigger>
                        <PopoverContent
                            w="280px"
                            sx={checkboxMenuPopoverContentSx}
                            data-filter-popover
                        >
                            <PopoverBody p={0}>
                                {renderDrillInPanel(cat.key, false)}
                                {renderFooter(false)}
                            </PopoverBody>
                        </PopoverContent>
                    </Popover>
                ))}

                <AddFilterMenu
                    isOpen={openState === 'addFilter'}
                    onToggle={() => togglePopover('addFilter')}
                    width={addFilterView ? '280px' : '220px'}
                    contentProps={{ 'data-filter-popover': '' }}
                >
                    {addFilterView === null ? (
                        <>
                            <CheckboxMenuSearch
                                value={filterSearch}
                                onChange={setFilterSearch}
                                placeholder="Search filters..."
                            />
                            <VStack align="stretch" spacing={0} p="6px">
                                {filteredCategories.map((cat) => (
                                    <MenuRow
                                        key={cat.key}
                                        kind="drillIn"
                                        summary={getCategorySummary(cat.key)}
                                        icon={<Icon as={cat.icon as React.ElementType} boxSize={4} />}
                                        onClick={() => setAddFilterView(cat.key)}
                                    >
                                        {cat.label}
                                    </MenuRow>
                                ))}
                            </VStack>
                            {renderFooter(true)}
                        </>
                    ) : (
                        <>
                            {renderDrillInPanel(addFilterView, true)}
                            {renderFooter(true)}
                        </>
                    )}
                </AddFilterMenu>
                {activeFilters.length > 0 && (
                    <ClearFiltersButton onClick={clearAllFilters} />
                )}
            </FilterBar>
        </Box>
    );
};

export default UsersAdvancedFilters;
